import "crqlar-react-common/dist/styles/common.scss";
import React, {useEffect, useState} from 'react';
import {Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Box, CircularProgress, CssBaseline, ThemeProvider} from "@mui/material";
import ResourceCategoryDetails from "./components/system/resourceCategories/ResourceCategoryDetails";
import ResourceCategoriesList from "./components/system/resourceCategories/ResourceCategoriesList";
import ResourcesList from "./components/system/resources/ResourcesList";
import ServiceCategoryDetails from "./components/system/serviceCategories/ServiceCategoryDetails";
import ServiceCategoriesList from "./components/system/serviceCategories/ServiceCategoriesList";
import ServiceDetails from "./components/system/services/ServiceDetails";
import ServicesList from "./components/system/services/ServicesList";
import SurveyDetails from "./components/surveys/details/SurveyDetails";
import SurveyEditor from "./components/surveys/editor/SurveyEditor";
import SurveyInvitationsListPage from "./components/surveys/invitations/SurveyInvitationsListPage";
import PublicSurveyPage from "./components/surveys/public/PublicSurveyPage";
import PublicDigitalSigningPage from "./components/digitalSigning/public/PublicDigitalSigningPage";
import DigitalSigningTemplatePage from "./components/digitalSigning/settings/DigitalSigningTemplatePage";
import DigitalSigningTemplatesPage from "./components/digitalSigning/settings/DigitalSigningTemplatesPage";
import DigitalSigningsPage from "./components/digitalSigning/list/DigitalSigningsPage";
import DigitalSigningPrintPage from "./components/digitalSigning/print/DigitalSigningPrintPage";
import SurveyList from "./components/surveys/list/SurveyList";
import SurveySubmissionsListPage from "./components/surveys/submissions/SurveySubmissionsListPage";
import {SURVEY_INVITATIONS_PATH, SURVEY_SUBMISSIONS_PATH} from "./components/surveys/shared/surveyListLinks";
import ProfileDetails from "./components/system/profile/ProfileDetails";
import ProfilesList from "./components/system/profile/ProfilesList";
import PaymentMethodsList from "./components/system/billing/paymentMethod/PaymentMethodsList";
import PaymentMethodDetails from "./components/system/billing/paymentMethod/PaymentMethodDetails";
import ReservationsList from "./components/system/reservation/ReservationsList";
import ReservationDetails from "./components/system/reservation/ReservationDetails";
import ContentCategoryDetails from "./components/system/contentCategories/ContentCategoryDetails";
import ContentCategoriesList from "./components/system/contentCategories/ContentCategoriesList";
import ContentDetails from "./components/system/content/ContentDetails";
import ContentList from "./components/system/content/ContentList";
import OrganizationDetails from "./components/system/organization/OrganizationDetails";
import SectorsList from "./components/system/sectors/SectorList";
import SectorDetails from "./components/system/sectors/SectorDetails";
import GuestCategoriesList from "./components/system/guestCategories/GuestCategoriesList";
import GuestCategoryDetails from "./components/system/guestCategories/GuestCategoryDetails";
import AgeCategoriesList from "./components/system/ageCategories/AgeCategoriesList";
import AgeCategoryDetails from "./components/system/ageCategories/AgeCategoryDetails";
import RatesList from "./components/system/rate/RatesList";
import RateDetails from "./components/system/rate/RateDetails";
import StartPage from "./components/system/StartPage";
import EliteConfigDetails from "./components/system/integrations/elite/details/EliteConfigDetails";
import FidelioConfigDetails from "./components/system/integrations/fidelio/details/FidelioConfigDetails";
import CasablancaConfigDetails from "./components/system/integrations/casablanca/details/CasablancaConfigDetails";
import ProtelConfigDetails from "./components/system/integrations/protel/details/ProtelConfigDetails";
import AvailabilityPlanner from "crqlar-react-common/dist/components/availability/AvailabilityPlanner";
import FoliosList from "./components/system/billing/folio/FolioList";
import FolioDetails from "./components/system/billing/folio/FolioDetails";
import AgendaList from "./components/system/agenda/AgendaList";
import AgendaDetails from "./components/system/agenda/AgendaDetails"
import GastrodatConfigDetails from "./components/system/integrations/gastrodat/details/GastrodatConfigDetails";
import NewsletterAccountDetails from "./components/system/newsletterAccount/NewsletterAccountDetails";
import ApiKeysList from "./components/system/apiKeys/ApiKeysList";
import ApiKeyDetails from "./components/system/apiKeys/ApiKeyDetails";
import Subscription from "./components/system/subscription/Subscription";
import {Consent} from "crqlar-react-common/dist/models/identity/consent.model";
import {AuthService} from "crqlar-react-common/dist/services/auth/auth.service";
import ConsentDialog from "./components/identity/ConsentDialog";
import LoginScreen from "./components/identity/LoginScreen";
import MyAccountDetails from "./components/identity/MyAccountDetails";
import SettingsLayout from "./components/system/layout/SettingsLayout";
import HomePage from "./components/home/HomePage";
import AllApps from "./components/marketplace/AllApps";
import GenericLandingPage from "./components/GenericLandingPage";
import PosTillLogin from "./components/pos/PosTillLogin";
import {DashboardShell} from "./components/layout/DashboardShell";
import {DocumentTitleManager} from "./components/layout/DocumentTitleManager";
import EditFloorplan from "./components/reservations/floorPlan/EditFloorplan";
import CreateNewService from "./components/reservations/newService/CreateNewService";
import ReservationSchedule from "./components/reservations/ReservationSchedule";
import Marketing from "./components/marketing/Marketing";
import Surveys from "./components/surveys/module/Surveys";
import AudienceMemberList from "./components/marketing/AudienceMemberList";
import AudienceGlobalConfigDetails from "./components/marketing/AudienceGlobalConfigDetails";
import Crm from "./components/crm/Crm";
import {useDispatch} from "react-redux";
import {AuthActions, useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useRedirect} from "./utils/redirect.util";
import BookingEngine from "./components/bookingEngine/BookingEngine";
import BookingEngineConfigsList from "./components/bookingEngine/configurations/BookingEngineConfigsList";
import BookingEngineDetails from "./components/bookingEngine/configurations/details/BookingEngineDetails";
// import BookingEngineInsights from "./components/insights/BookingEngineInsights";
import StripeConfigurationDetails from "./components/system/integrations/stripe/StripeConfigurationDetails";
import HobexConfigurationDetails from "./components/system/integrations/hobex/HobexConfigurationDetails";
import {useCheckSubscription} from "crqlar-react-common/dist/utils/useCheckSubscription";
import AsaConfigDetails from "./components/system/integrations/asa/details/AsaConfigDetails";
import OhipConfigDetails from "./components/system/integrations/ohip/details/OhipConfigDetails";
import SimphonyConfigDetails from "./components/system/integrations/simphony/details/SimphonyConfigDetails";
import {Settings} from "luxon";
import TransactionalMailsSettings from "./components/system/newsletterAccount/TransactionalMailsSettings";
import MarketingStatisticsList from "./components/system/marketingStatistics/MarketingStatisticsList";
import MetaConnectorSettings from "./components/system/meta/MetaConnectorSettings";
import ProtectedRoute from "crqlar-react-common/dist/utils/route/ProtectedRoute";
import {resolvePermittedApp} from "crqlar-react-common/dist/utils/route/permittedApp";
import {useMarketplaceApps} from "crqlar-react-common/dist/utils/hooks/app/useMarketplaceApps";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {AppSku} from "crqlar-react-common/dist/models/app/appSku";
import Unauthorized from "./pages/Unauthorized";
import CrossSalePreviewPage from "./components/crossSale/CrossSalePreviewPage";
import FolioPrintout from "./components/system/billing/folio/FolioPrintout.component";
import TaxRatesList from "./components/system/taxRate/TaxRatesList";
import TaxRateDetails from "./components/system/taxRate/TaxRateDetails";
import {VoiceNote} from "./components/voiceNote/VoiceNote";
import ResetPasswordScreen from "./components/identity/ResetPasswordSceen";
import ChangePasswordScreen from "./components/identity/ChangePasswordScreen";
import FiasConfigDetails from "./components/system/integrations/fias/details/FiasConfigDetails";
import UserList from "./components/user/UserList";
import SettingsUserDetails from "./components/system/layout/SettingsUserDetails";
import MappingCodesDetails from "./components/system/mapping/MappingCodesDetails";
import MappingCodesList from "./components/system/mapping/MappingCodesList";
import ApaleoConfigDetails from "./components/system/integrations/apaleo/details/ApaleoConfigDetails";
import MewsConfigDetails from "./components/system/integrations/mews/details/MewsConfigDetails";
import IncertConfigDetails from "./components/system/integrations/incert/details/IncertConfigDetails";
import GuestAppUsersList from "./components/system/guestApp/GuestAppUsersList";
import GuestAppUserDetails from "./components/system/guestApp/GuestAppUserDetails";
import CorrespondenceMessagesList from "./components/system/correspondence/CorrespondenceMessagesList";
import CorrespondenceMessageDetails from "./components/system/correspondence/CorrespondenceMessageDetails";
import ReservationConfirmationPrintout from "./components/reservations/print/ReservationConfirmationPrintout";
import AutomationDetails from "./components/marketing/automations/AutomationDetails";
import AutomationExecutionsPage from "./components/marketing/automations/executions/AutomationExecutionsPage";
import LegacyExecutionsRedirect from "./components/marketing/automations/executions/LegacyExecutionsRedirect";
import ResourceDetails from "./components/system/resources/ResourceDetails";
import PrintTemplatesList from "./components/system/print/PrintTemplatesList";
import PrintTemplateDetails from "./components/system/print/PrintTemplateDetails";
import PrintoutsList from "./components/printouts/list/PrintoutsList";
import PrintoutDetails from "./components/printouts/details/PrintoutDetails";
import PrintoutEditor from "./components/printouts/editor/PrintoutEditor";
import PrintoutPrintHarness from "./components/printouts/print/PrintoutPrintHarness";
import PrintoutPreview from "./components/printouts/print/PrintoutPreview";
import FormDetails from "./components/marketing/forms/FormDetails";
import FormsList from "./components/marketing/forms/list/FormList";
import PublicFormPreview from "./components/marketing/forms/preview/PublicFormPreview";
import PublicAgendaPrintout from "./components/reservations/print/PublicAgendaPrintout";
import DeduplicationDashboard from "./components/system/deduplication/DeduplicationDashboard";
import ProfileClustersList from "./components/system/profileClusters/ProfileClustersList";
import ProfileClusterDetails from "./components/system/profileClusters/ProfileClusterDetails";
import LogsList from "./components/system/logs/LogsList";
import {getSelfServiceTrialStatus} from "./components/TrialBanner";
import {syncMixpanelAuth} from "./utils/analytics/mixpanelAuth";
import NoShowPaymentPage from "crqlar-react-common/dist/components/booking-widget/NoShowPaymentPage";
import {AssistantProvider} from "./components/assistant/AssistantPanelContext";
import FormSubmissions from "./components/marketing/forms/FormSubmissions";
import TestPage from "./components/test/TestPage";
import SettingsPageHeader from "./components/system/layout/SettingsPageHeader";
import {useTranslation} from "react-i18next";
import {createDashboardTheme} from "./theme";
import {applyUserLanguage} from "./utils/applyUserLanguage";

// The survey lists moved into the settings. Old links keep their period, search and filters; the
// per-survey ones also become the Survey filter.
const SurveyListRedirect = ({path}: { path: string }) => {
    const {id} = useParams()
    const {search} = useLocation()

    const params = new URLSearchParams(search)
    if (id) params.set('surveyId', id)
    // The settings lists keep their period in the filters, not in from/to.
    params.delete('from')
    params.delete('to')
    const query = params.toString()

    return <Navigate to={query ? `${path}?${query}` : path} replace/>
}

const SurveyRoutes = {
    system: (
        <>
            <Route path="surveys/:id" element={<SurveyDetails/>}/>
            <Route path="surveys" element={<SurveyList/>}/>
            <Route path="surveySubmissions" element={<SurveySubmissionsListPage/>}/>
            <Route path="surveyInvitations" element={<SurveyInvitationsListPage/>}/>
        </>
    ),

    fullscreen: (
        <>
            <Route path="/system/surveys/:id/submissions" element={<SurveyListRedirect path={SURVEY_SUBMISSIONS_PATH}/>}/>
            <Route path="/system/surveys/:id/invitations" element={<SurveyListRedirect path={SURVEY_INVITATIONS_PATH}/>}/>
            <Route
                path="/system/surveys/:id/editor"
                element={
                    <ProtectedRoute protectionType={'settings'}>
                        <SurveyEditor/>
                    </ProtectedRoute>
                }
            />
        </>
    ),

    public: (
        <>
            <Route path="survey/i/:token" element={<PublicSurveyPage/>}/>
            <Route path="survey/:surveyId" element={<PublicSurveyPage/>}/>
        </>
    )
}

/**
 * Simphony till sign-in. Unauthenticated, and deliberately not under `/public`: the URL is typed
 * into Oracle's EMC by a property and should stay as short as possible.
 * See `features/simphony-pos-till-authentication.md`.
 */
const posRoutes = (
    <Route path="/pos" element={<PosTillLogin/>}/>
)

const publicRoutes = (
    <Route
        path={"/public"}
        element={
            <Box><Outlet/></Box>
        }
    >
        <Route path="form/:id" element={<PublicFormPreview/>}/>
        <Route path="noshow/:token" element={<NoShowPaymentPage/>}/>
        <Route path="confirm" element={<PublicDigitalSigningPage/>}/>
        {SurveyRoutes.public}
    </Route>
)

// Keep the former editor URL working for existing links.
const PrintoutEditorNextRedirect = () => {
    const {id} = useParams()

    return <Navigate to={`/system/printouts/${id}/editor`} replace/>
}

// Survey insights moved out of marketing into the surveys module.
const SurveyInsightsRedirect = () => {
    const {pathname} = useLocation()
    const organizationApps = useOrganizationApps()
    const marketplaceApps = useMarketplaceApps()

    const appId = resolvePermittedApp({
        organizationApps,
        marketplaceApps,
        pathname,
        matcher: {sku: AppSku.SURVEYS},
        legacyName: "Surveys",
    })?.id

    return <Navigate to={appId ? `/surveys/insights/${appId}` : "/"} replace/>
}

const SettingsAvailabilityRoute = () => {
    const {t} = useTranslation()
    const {entityId} = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo
    const backTo = returnTo || (entityId ? `/system/resources/${entityId}` : '/system/resources')

    return (
        <>
            <SettingsPageHeader title={t('Availability')} back={() => navigate(backTo)}/>
            <AvailabilityPlanner/>
        </>
    )
}

const systemRoutes = (
    <Route
        path={"/system"}
        element={
            // Here we protect the whole system route
            <ProtectedRoute
                protectionType={'settings'}
            >
                <SettingsLayout/>
            </ProtectedRoute>
        }
    >
        <Route path="availability/:entityId" element={<SettingsAvailabilityRoute/>}/>
        <Route path="rates/:id" element={<RateDetails/>}/>
        <Route path="rates" element={<RatesList/>}/>
        <Route path="taxRates/:id" element={<TaxRateDetails/>}/>
        <Route path="taxRates" element={<TaxRatesList/>}/>

        <Route path="resourceCategories/:id" element={<ResourceCategoryDetails/>}/>
        <Route path="resourceCategories" element={<ResourceCategoriesList/>}/>
        <Route path="resources/:id" element={<ResourceDetails/>}/>
        <Route path="resources" element={<ResourcesList/>}/>

        <Route path="serviceCategories/:id" element={<ServiceCategoryDetails/>}/>
        <Route path="serviceCategories" element={<ServiceCategoriesList/>}/>
        <Route path="services/:id" element={<ServiceDetails/>}/>
        <Route path="services" element={<ServicesList/>}/>
        {SurveyRoutes.system}
        <Route path="ageCategories/:id" element={<AgeCategoryDetails/>}/>
        <Route path="ageCategories" element={<AgeCategoriesList/>}/>
        <Route path="guestCategories/:id" element={<GuestCategoryDetails/>}/>
        <Route path="guestCategories" element={<GuestCategoriesList/>}/>
        <Route path="printTemplates/:id" element={<PrintTemplateDetails/>}/>
        <Route path="printTemplates" element={<PrintTemplatesList/>}/>
        <Route path="printouts/:id" element={<PrintoutDetails/>}/>
        <Route path="printouts" element={<PrintoutsList/>}/>
        {/* Keep the former list URL working for existing links. */}
        <Route path="printouts-next" element={<Navigate to="/system/printouts" replace/>}/>

        <Route path="profiles/:id" element={<ProfileDetails/>}/>
        <Route path="profiles" element={<ProfilesList/>}/>

        <Route path="agenda/:id" element={<AgendaDetails/>}/>
        <Route path="agenda" element={<AgendaList/>}/>

        <Route path="deduplication" element={<DeduplicationDashboard/>}/>

        <Route path="profileClusters/:id" element={<ProfileClusterDetails/>}/>
        <Route path="profileClusters" element={<ProfileClustersList/>}/>

        <Route path="guestAppUsers/:id" element={<GuestAppUserDetails/>}/>
        <Route path="guestAppUsers" element={<GuestAppUsersList/>}/>

        <Route path="paymentMethods/:id" element={<PaymentMethodDetails/>}/>
        <Route path="paymentMethods" element={<PaymentMethodsList/>}/>
        <Route path="folios/:id" element={<FolioDetails/>}/>
        <Route path="folios" element={<FoliosList/>}/>
        {/* One route with an optional id: the list stays mounted while its details dialog opens and closes */}
        <Route path="signatures/:id?" element={<DigitalSigningsPage/>}/>
        <Route path="digitalSigning/:id" element={<DigitalSigningTemplatePage/>}/>
        <Route path="digitalSigning" element={<DigitalSigningTemplatesPage/>}/>

        <Route path="reservations/:id" element={<ReservationDetails/>}/>
        <Route path="reservations" element={<ReservationsList/>}/>

        <Route path="correspondenceMessages/:id" element={<CorrespondenceMessageDetails/>}/>
        <Route path="correspondenceMessages" element={<CorrespondenceMessagesList/>}/>

        <Route path="contentCategories/:id" element={<ContentCategoryDetails/>}/>
        <Route path="contentCategories" element={<ContentCategoriesList/>}/>
        <Route path="contents/:id" element={<ContentDetails/>}/>
        <Route path="contents" element={<ContentList/>}/>

        {/*<Route path="team/:id" element={<UserAccessDetails/>}/>*/}
        <Route path="team/:id" element={<SettingsUserDetails/>}/>
        <Route path="team" element={<UserList/>}/>
        <Route path="organization" element={<OrganizationDetails/>}/>

        <Route path="mappingCodes/:id" element={<MappingCodesDetails/>}/>
        <Route path="mappingCodes" element={<MappingCodesList/>}/>

        <Route
            path="billing"
            element={
                <ProtectedRoute protectionType={'billing'}>
                    <Subscription/>
                </ProtectedRoute>
            }
        />
        <Route path="apiKeys/:id" element={<ApiKeyDetails/>}/>
        <Route path="apiKeys" element={<ApiKeysList/>}/>

        <Route path="sectors/:id" element={<SectorDetails/>}/>
        <Route path="sectors" element={<SectorsList/>}/>

        <Route path="logs" element={<LogsList/>}/>

        {/*As we can't get the appId from settings sidebar - i just removed that from the path*/}
        <Route path='audienceGlobalConfig' element={<AudienceGlobalConfigDetails/>}/>
        <Route path="newsletterAccount" element={<NewsletterAccountDetails/>}/>
        <Route path="marketingStatistics" element={<MarketingStatisticsList/>}/>
        <Route path="metaConnector" element={<MetaConnectorSettings/>}/>

        {/*Optional URL param - it can be accessed from communication dialog (URL param needed) and Sidebar settings (-)*/}
        <Route path="transactionalMailSettings/:sectorId?" element={<TransactionalMailsSettings/>}/>

        <Route path="integrations/elite" element={<EliteConfigDetails/>}/>
        <Route path="integrations/fidelio" element={<FidelioConfigDetails/>}/>
        <Route path="integrations/casablanca" element={<CasablancaConfigDetails/>}/>
        <Route path="integrations/protel" element={<ProtelConfigDetails/>}/>
        <Route path="integrations/gastrodat" element={<GastrodatConfigDetails/>}/>
        <Route path="integrations/stripe" element={<StripeConfigurationDetails/>}/>
        <Route path="integrations/hobex" element={<HobexConfigurationDetails/>}/>
        <Route path="integrations/asa" element={<AsaConfigDetails/>}/>
        <Route path="integrations/ohip" element={<OhipConfigDetails/>}/>
        <Route path="integrations/simphony" element={<SimphonyConfigDetails/>}/>
        <Route path="integrations/mews" element={<MewsConfigDetails/>}/>
        <Route path="integrations/fias" element={<FiasConfigDetails/>}/>
        <Route path="integrations/apaleo" element={<ApaleoConfigDetails/>}/>
        <Route path="integrations/incert" element={<IncertConfigDetails/>}/>

        <Route path="forms/:id/submissions" element={<FormSubmissions/>}/>
        <Route path="forms/:id" element={<FormDetails/>}/>
        <Route path="forms" element={<FormsList/>}/>

        <Route path="/system/" element={<StartPage/>}/>

    </Route>
)

const marketPlaceRoutes = (
    <Route path="/marketplace" element={<GenericLandingPage/>}>
        <Route
            path="/marketplace"
            element={
                <ProtectedRoute
                    protectionType={'marketplace'}
                >
                    <AllApps/>
                </ProtectedRoute>
            }
        />
    </Route>
)

const identityRoutes = (
    <Route path="/identity">
        <Route path="login" element={<LoginScreen/>}/>
        <Route path="password/reset" element={<ResetPasswordScreen/>}/>
        <Route path="password/change" element={<ChangePasswordScreen/>}/>
    </Route>
)

const myAccountRoutes = (
    <Route path="/identity/myAccount" element={<GenericLandingPage/>}>
        <Route index element={<MyAccountDetails/>}/>
    </Route>
)

const SECTOR_PATHS = ['restaurant', 'spa', 'accommodation', 'other', 'weekly-planner']

const reservationSectorRoutes = SECTOR_PATHS.map(path => (
    <Route
        key={path}
        path={`/${path}`}
        element={<GenericLandingPage/>}
    >
        <Route path={":appId/:sectorId/newService/:entityId"} element={<CreateNewService/>}/>
        <Route
            path=":tab/:appId"
            element={
                <ProtectedRoute protectionType={'apps'}>
                    <ReservationSchedule/>
                </ProtectedRoute>
            }
        />
    </Route>
))

/**
 * The flow-level runs routes are children of the editor route: `AutomationDetails` renders them
 * through its own `<Outlet/>`, as a full-screen layer over the canvas, so opening the runs of the
 * flow being edited keeps the editor mounted with its unsaved changes, and the parent's
 * `ProtectedRoute` covers the children.
 *
 * The org-level `:appId/runs/:executionId` (every flow's runs, no flow being edited) has no editor
 * to render into and stays a route of its own. It cannot collide with the nested pair: its fourth
 * segment is the literal `runs`, theirs is the `:flowId`.
 *
 * Until 2026-10-07 the same pages lived under `executions`; those addresses forward to `runs`
 * (`LegacyExecutionsRedirect`), so old bookmarks and links keep working.
 */
const AutomationRoutes = {
    fullscreen: (
        <>
            <Route
                path="/marketing/automations/:appId/runs/:executionId"
                element={
                    <ProtectedRoute
                        protectionType={'apps'}
                        permittedApp={{sku: AppSku.MARKETING}}
                        permittedAppName={'Marketing & Campaigns'}
                    >
                        <AutomationExecutionsPage/>
                    </ProtectedRoute>
                }
            />
            <Route path="/marketing/automations/:appId/executions/:executionId" element={<LegacyExecutionsRedirect/>}/>
            <Route
                path="/marketing/automations/:appId/:flowId"
                element={
                    <ProtectedRoute
                        protectionType={'apps'}
                        permittedApp={{sku: AppSku.MARKETING}}
                        permittedAppName={'Marketing & Campaigns'}
                    >
                        <AutomationDetails/>
                    </ProtectedRoute>
                }
            >
                <Route path="runs" element={<AutomationExecutionsPage/>}/>
                <Route path="runs/:executionId" element={<AutomationExecutionsPage/>}/>
                <Route path="executions/*" element={<LegacyExecutionsRedirect/>}/>
            </Route>
            <Route
                path="/surveys/automations/:appId/runs/:executionId"
                element={
                    <ProtectedRoute
                        protectionType={'apps'}
                        permittedApp={{sku: AppSku.SURVEYS}}
                        permittedAppName={'Surveys'}
                    >
                        <AutomationExecutionsPage/>
                    </ProtectedRoute>
                }
            />
            <Route path="/surveys/automations/:appId/executions/:executionId" element={<LegacyExecutionsRedirect/>}/>
            <Route
                path="/surveys/automations/:appId/:flowId"
                element={
                    <ProtectedRoute
                        protectionType={'apps'}
                        permittedApp={{sku: AppSku.SURVEYS}}
                        permittedAppName={'Surveys'}
                    >
                        <AutomationDetails/>
                    </ProtectedRoute>
                }
            >
                <Route path="runs" element={<AutomationExecutionsPage/>}/>
                <Route path="runs/:executionId" element={<AutomationExecutionsPage/>}/>
                <Route path="executions/*" element={<LegacyExecutionsRedirect/>}/>
            </Route>
        </>
    )
}

const marketingRoutes = (
    <Route
        path='/marketing'
        element={
            <ProtectedRoute
                protectionType={'apps'}
                permittedApp={{sku: AppSku.MARKETING}}
                permittedAppName={'Marketing & Campaigns'}
            >
                <GenericLandingPage/>
            </ProtectedRoute>
        }>
        <Route path="audiences/:appId" element={<Marketing/>}/>
        <Route path="audience/members" element={<AudienceMemberList/>}/>
        <Route path="newsletter/:appId" element={<Marketing/>}/>
        <Route path="lifecycle-mails/:appId" element={<Marketing/>}/>
        <Route path="automations/:appId" element={<Marketing/>}/>
        <Route path="facebook-instagram-ads/:appId" element={<Marketing/>}/>
        <Route path="insights" element={<Marketing/>}/>
        {/* Keep the former survey insights URL working for existing links. */}
        <Route path="survey-insights" element={<SurveyInsightsRedirect/>}/>
    </Route>
)

const surveysRoutes = (
    <Route
        path='/surveys'
        element={
            <ProtectedRoute
                protectionType={'apps'}
                permittedApp={{sku: AppSku.SURVEYS}}
                permittedAppName={'Surveys'}
            >
                <GenericLandingPage/>
            </ProtectedRoute>
        }>
        <Route path="daily/:appId" element={<Surveys/>}/>
        <Route path="todo/:appId" element={<Surveys/>}/>
        <Route path="overview/:appId" element={<Surveys/>}/>
        <Route path="automations/:appId" element={<Surveys/>}/>
        <Route path="submissions/:appId" element={<Surveys/>}/>
        <Route path="insights/:appId" element={<Surveys/>}/>
        <Route path="evaluation/:appId" element={<Surveys/>}/>
        {/* These lists moved to the system settings; keep the former module tab URLs working. */}
        <Route path="list/:appId" element={<Navigate to="/system/surveys" replace/>}/>
        <Route path="invitations/:appId" element={<SurveyListRedirect path={SURVEY_INVITATIONS_PATH}/>}/>
    </Route>
)

const crmRoutes = (
    <Route>
        <Route
            path="/crm"
            element={
                <ProtectedRoute
                    protectionType={'apps'}
                    permittedApp={{sku: AppSku.CRM}}
                    permittedAppName={'CRM'}
                >
                    <GenericLandingPage/>
                </ProtectedRoute>
            }
        >
            <Route path="daily/:appId" element={<Crm/>}/>
            <Route path="guestList/:appId" element={<Crm/>}/>
            <Route path="insights/:appId" element={<Crm/>}/>
            <Route path="demographics/:appId" element={<Crm/>}/>
        </Route>
    </Route>
)

const bookingEngineRoutes = (
    <Route
        path='/bookingEngine'
        element={
            <ProtectedRoute
                protectionType={'apps'}
                permittedApp={{sku: AppSku.BOOKING_ENGINE}}
                permittedAppName={'Booking engine'}
            >
                <GenericLandingPage/>
            </ProtectedRoute>
        }
    >
        <Route element={<BookingEngine/>}>
            <Route index element={<Navigate replace to="configurations"/>}/>
            <Route path="configurations" element={<BookingEngineConfigsList/>}/>
            <Route path="configurations/:id" element={<BookingEngineDetails/>}/>
            {/*<Route path="insights" element={<BookingEngineInsights/>}/>*/}
        </Route>
    </Route>
)

const voiceNoteRoutes = (
    <Route
        path='/voiceNote'
        element={
            <ProtectedRoute
                protectionType={'apps'}
                permittedApp={{sku: AppSku.VOICE_NOTE}}
                permittedAppName={'Voice note'}
            >
                <GenericLandingPage/>
            </ProtectedRoute>
        }
    >
        <Route path=":appId/*" element={<VoiceNote/>}/>
    </Route>
)

const mvpRoutes = (
    <Route path='/mvp' element={<GenericLandingPage/>}>
        {/*Hide chatwoot mvp for now as it is still underdeveloped*/}
        {/*<Route path="messages" element={<ChatwootAgentView/>}/>*/}
    </Route>
)

const printoutRoutes = (
    <Route>
        <Route path="/print/folio/:id" element={<FolioPrintout/>}/>
        <Route path="/print/signature/:id" element={<DigitalSigningPrintPage/>}/>
        <Route
            path="/print/reservationConfirmation/:reservationId?/:folioItemId?/:language?"
            element={<ReservationConfirmationPrintout/>}
        />
        <Route path="/print/agenda/:agendaId" element={<PublicAgendaPrintout/>}/>
        <Route path="/print/printout/:type/:id" element={<PrintoutPreview/>}/>
        {/* Development-only print renderer. */}
        {process.env.NODE_ENV === "development" && (
            <Route path="/print/printout-harness" element={<PrintoutPrintHarness/>}/>
        )}
    </Route>
)

/**
 * Blurred previews of cross-selling apps the organization is not subscribed to. Deliberately not behind
 * `ProtectedRoute`: the page mounts no module component and fetches no module data, and it forwards to
 * the real app when the organization is subscribed.
 */
const crossSalePreviewRoutes = (
    <Route path="/preview" element={<GenericLandingPage/>}>
        <Route path=":slug" element={<CrossSalePreviewPage/>}/>
    </Route>
)

const unauthorizedRoutes = (
    <Route
        path="/unauthorized"
        element={<GenericLandingPage/>}
    >
        <Route index element={<Unauthorized/>}/>
    </Route>
)


const routes = (
    <Routes>
        {/* Reachable without a session — never behind the splash */}
        {publicRoutes}
        {posRoutes}
        {identityRoutes}
        {printoutRoutes}
        {unauthorizedRoutes}

        {/* Authenticated app — DashboardShell mounts once and owns the splash */}
        <Route element={<DashboardShell><Outlet/></DashboardShell>}>
            {/* Fullscreen floorplan editor: authenticated, but outside GenericLandingPage constraints. */}
            <Route path="/restaurant/floorplan/:appId/:sectorId/:restaurantId/edit" element={<EditFloorplan/>}/>
            {systemRoutes}
            {marketPlaceRoutes}
            {reservationSectorRoutes}
            {marketingRoutes}
            {surveysRoutes}
            {crmRoutes}
            {bookingEngineRoutes}
            {voiceNoteRoutes}
            {crossSalePreviewRoutes}
            {mvpRoutes}
            {myAccountRoutes}
            <Route path="/test/*" element={<TestPage/>}/>
            <Route path="/system/test/uiElements" element={<Navigate to="/test/ui-elements" replace/>}/>
            <Route path="/system/test/newDatePicker" element={<Navigate to="/test/date-pickers" replace/>}/>
            <Route path="/system/test/autocomplete" element={<Navigate to="/test/autocomplete" replace/>}/>
            {/* Fullscreen printout content editor: inside the shell (keeps the auth splash) but
                outside systemRoutes/SettingsLayout, so it renders without the settings sidebar. */}
            <Route
                path="/system/printouts/:id/editor"
                element={
                    <ProtectedRoute protectionType={'settings'}>
                        <PrintoutEditor/>
                    </ProtectedRoute>
                }
            />
            <Route path="/system/printouts/:id/editor-next" element={<PrintoutEditorNextRedirect/>}/>
            {SurveyRoutes.fullscreen}
            {AutomationRoutes.fullscreen}
            <Route path="/" element={<GenericLandingPage/>}>
                <Route index element={<HomePage/>}/>
            </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace/>}/>

    </Routes>
)

const App = () => {

    const location = useLocation()

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const redirect = useRedirect()

    const [searchParams] = useSearchParams()
    const adminSSO = searchParams.get('adminSSO') as string | undefined;
    const redirectTo = searchParams.get('redirectTo') ? decodeURI(searchParams.get('redirectTo')!) : undefined;

    const [tokenValidationInProgress, setTokenValidationInProgress] = useState(true)
    const [consentsToAccept, setConsentsToAccept] = useState<Consent[]>([])

    const organization = useOrganization();

    const validateToken = async () => {

        // If this is a self-service magic link, wipe the existing session entirely
        // so the link credentials take over instead of the current user.
        if (searchParams.get('utm_source') === 'self_service_signup') {
            localStorage.clear()
            setTokenValidationInProgress(false)
            return
        }

        if (adminSSO) {
            console.log("-> admin login")
            // clear the existing token to start a fresh session (to sync the organization branding)
            localStorage.removeItem("token")

            localStorage.setItem('token', adminSSO)
            const response = await AuthService.validateToken()
            if (response?.token) {
                localStorage.setItem('token', response.token)
                dispatch({
                    type: AuthActions.SET_USER,
                    payload: {user: response.user, organization: response.organization}
                })
                syncMixpanelAuth(response)
                applyUserLanguage(response.user)

                navigate(redirectTo || '/')
            }
            setTokenValidationInProgress(false)

            setTokenValidationInProgress(false)
            return
        }

        const tokenToCheck = localStorage.getItem('token')
        let user
        let newConsentsToAccept: Consent[] | undefined
        if (tokenToCheck) {
            console.log('-> authentication: step 2')
            try {
                const response = await AuthService.validateToken()
                user = response?.user
                newConsentsToAccept = response?.consentsToAccept
                const token = response?.token
                if (token) {
                    localStorage.setItem('token', token)

                    dispatch({
                        type: AuthActions.SET_USER,
                        payload: {user: response.user, organization: response.organization}
                    })
                    syncMixpanelAuth(response)
                    applyUserLanguage(response.user)
                    Settings.defaultZone = (response?.organization?.address?.timeZone as string) ?? 'Europe/Vienna'
                } else {
                    localStorage.removeItem('token')
                    syncMixpanelAuth(undefined)

                    dispatch({
                        type: AuthActions.SET_USER,
                        payload: undefined
                    })
                }


            } catch (e) {
                console.error(e)
                localStorage.removeItem('token')
                syncMixpanelAuth(undefined)
            }
        }
        setTokenValidationInProgress(false)

        if (user) {
            console.log('User is logged in')

            if (newConsentsToAccept?.length) {
                setConsentsToAccept(newConsentsToAccept)
            } else {
                redirect()
                // console.log('No consents to accept, will redirect to', redirectTo || '/')
                // navigate(redirectTo || window.location.pathname || '/')
            }
        } else {
            console.log('User is not logged in', window.location.pathname)

            const isPublicIdentityPage = [
                "/public/",
                // The till arrives with no token by design — /pos is what fetches one
                "/pos",
                "/identity/login",
                "/identity/password/change",
                "/identity/password/reset"
            ].some(path => window.location.pathname.startsWith(path))

            if (!isPublicIdentityPage) {
                console.log('Redirecting to login', "/identity/login?redirectTo=" + window.location.pathname)
                navigate("/identity/login?redirectTo=" + window.location.pathname)
            }
        }
    }

    useEffect(() => {
        validateToken()
    }, [])

    //   // for testing purposes we override an already existing api theme with not set
    // const muiTheme = createDashboardTheme(organization?.theme);
    const muiTheme = createDashboardTheme();

    // self service organizations should show a banner
    const checkSubscription = useCheckSubscription(process.env.NODE_ENV !== "development" && !tokenValidationInProgress && !!organization)

    const selfServiceTrialStatus = getSelfServiceTrialStatus(organization);
    // const bannerVisible = !!selfServiceTrialStatus;

    // useEffect(() => {
    //     const offset = bannerVisible ? `${TRIAL_BANNER_HEIGHT}px` : '0px';
    //     document.documentElement.style.setProperty('--banner-offset', offset);
    //     return () => { document.documentElement.style.removeProperty('--banner-offset'); };
    // }, [bannerVisible]);

    const subscriptionExcludedRoutes = [
        '/system/billing',
        '/identity/myAccount',
    ];

    // TEMPORARILY DISABLED: the subscription gate is switched off so every customer can use the
    // app even without an active subscription. Set this back to `true` to re-enable it.
    const subscriptionGateEnabled = false;

    const shouldShowSubscriptionUI = subscriptionGateEnabled &&
        !subscriptionExcludedRoutes.includes(location.pathname) &&
        !checkSubscription.query.isLoading &&
        (organization?.selfService !== true || selfServiceTrialStatus?.isExpired === true) &&
        checkSubscription.query.data?.hasSubscription === false;


    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline/>
            <DocumentTitleManager>
                {tokenValidationInProgress ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh',
                            width: '100vw',
                        }}
                    >
                        <CircularProgress/>
                    </Box>
                ) : (
                    <AssistantProvider persist={false}>
                        <div className="App">
                            {/* <TrialBanner organization={organization}/> */}
                            {routes}

                            <ConsentDialog
                                open={!!consentsToAccept.length}
                                consentsToAccept={consentsToAccept}
                                onAccept={() => {
                                    setConsentsToAccept([])
                                    if (!redirect()) {
                                        navigate('/')
                                    }
                                }}
                            />

                            {shouldShowSubscriptionUI && checkSubscription.noSubscriptionUI}

                        </div>
                    </AssistantProvider>
                )}
            </DocumentTitleManager>
        </ThemeProvider>
    )

}

export default App;
